import React, { createContext, PropsWithChildren, useCallback, useEffect, useReducer } from 'react';

import { getWindow } from '../services/windowService';

const safeParse = (rawValue?: string | null) => {
  if (rawValue === null || typeof rawValue === 'undefined') {
    return undefined;
  }
  try {
    return JSON.parse(rawValue);
  } catch {
    return rawValue;
  }
};

const wnd = getWindow();
const { localStorage } = wnd;

export enum SyncState {
  InSync = 'in-sync',
  Finished = 'finished',
}

type LocalStorage = {
  data: Record<string, any>;
  syncState: SyncState;
};

const initialLocalStorage: LocalStorage = {
  data: { ...localStorage },
  syncState: SyncState.InSync,
};

enum Actions {
  SET_ITEM,
  REMOVE_ITEM,
  CLEAR,
  SYNC,
}

const setItem = (ls: LocalStorage, payload: any): LocalStorage => {
  return {
    ...ls,
    data: {
      ...ls.data,
      ...payload,
    },
  };
};
const removeItem = (ls: LocalStorage, payload: string): LocalStorage => {
  const next = { ...ls.data };
  delete next[payload];
  return {
    ...ls,
    data: next,
  };
};

const clear = (): LocalStorage => {
  return { syncState: SyncState.Finished, data: {} };
};

const syncWithLocalStorage = (): LocalStorage => {
  const data = Object.entries<string>(localStorage).reduce<Record<string, string>>((acc, pair) => {
    acc[pair[0]] = safeParse(pair[1]);
    return acc;
  }, {});
  return { syncState: SyncState.Finished, data };
};

type Action =
  | { type: Actions.SET_ITEM; payload: any }
  | { type: Actions.CLEAR; payload?: never }
  | { type: Actions.SYNC; payload?: never }
  | { type: Actions.REMOVE_ITEM; payload: string };

const reducer = (ls: LocalStorage, action: Action) => {
  const { type, payload } = action;
  switch (type) {
    case Actions.SET_ITEM:
      ls = setItem(ls, payload);
      break;
    case Actions.CLEAR:
      ls = clear();
      break;
    case Actions.SYNC:
      ls = syncWithLocalStorage();
      break;
    case Actions.REMOVE_ITEM:
      ls = removeItem(ls, payload);
      break;
    default:
      console.error(`Action ${(action as any).type} is not implemented.`);
      break;
  }
  return ls;
};

interface Props {
  setValue: (key: string) => <T>(value: T) => void;
  removeValue: (key: string) => () => void;
  state: LocalStorage;
}

export const LocalStorageContext = createContext<Props | null>(null);

export const LocalStorageContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialLocalStorage);

  useEffect(() => {
    const storageListener = (e: StorageEvent) => {
      const { key, newValue } = e;
      if (key === null) {
        dispatch({ type: Actions.CLEAR });
        return;
      }
      dispatch({
        type: Actions.SET_ITEM,
        payload: { [key]: safeParse(newValue) },
      });
    };

    wnd.addEventListener('storage', storageListener);
    dispatch({ type: Actions.SYNC });
    return () => {
      wnd.removeEventListener('storage', storageListener);
    };
  }, []);

  const setValue = useCallback((key: string) => {
    return <T,>(value: T) => {
      try {
        const valueToInsert = typeof value === 'object' ? JSON.stringify(value) : String(value);
        localStorage.setItem(key, valueToInsert);
        dispatch({ type: Actions.SET_ITEM, payload: { [key]: value } });
      } catch (error) {
        console.error(error);
      }
    };
  }, []);

  const removeValue = useCallback(
    (key: string) => () => {
      localStorage.removeItem(key);
      dispatch({ type: Actions.REMOVE_ITEM, payload: key });
    },
    [],
  );

  return (
    <LocalStorageContext.Provider value={{ setValue, removeValue, state }}>
      {children}
    </LocalStorageContext.Provider>
  );
};
